import React from "react";
import { useHistory } from "react-router";
import { compose } from "redux";

import withAnalysisCategories from "../../../../../../hocs/withAnalysisCategories";
import withManualStrategies from "../../../../../../hocs/withManualStrategies";
import withPreparedSectionSlugList from "../../../../../../hocs/withPreparedSectionSlugList";
import withTranslations from "../../../../../../hocs/withTranslations";
import { NOTIFICATION_TYPES } from "../../../../../../lib/getStockPageSectionFromNotificationType";
import { STOCK_NOTIFICATION_TYPE } from "../../../../../../lib/notifications/constants";
import { notificationItemFactory } from "../../../../navigation/NotificationItem/notificationItemFactory";
import FormatCurrency from "../../Homepage/components/FormatCurrency";
import AlertIcon from "../assets/alert.svg";
import AnalysisIcon from "../assets/analysis.svg";
import CoffeeIcon from "../assets/coffee.svg";
import DealIcon from "../assets/deal.svg";
import EducationIcon from "../assets/education.svg";
import FeedbackIcon from "../assets/feedback.svg";
import NewspaperIcon from "../assets/newspaper.svg";
import OfferIcon from "../assets/offer.svg";
import RecommendationIcon from "../assets/recommendation.svg";
import ScaleIcon from "../assets/scale.svg";
import Squaber from "../assets/squaber.svg";
import TurtleIcon from "../assets/turtle.svg";
import TurtleWithSquaberIcon from "../assets/turtle-with-squaber-icon.svg";
import VideoIcon from "../assets/video.svg";
import WebinarIcon from "../assets/webinar.svg";
import HighsAndLowsIcon from "../assets/highs-and-lows-icon.svg";
import HighsAndLowsSidIcon from "../assets/highs-and-lows-sid-icon.svg";
import SupplyDemandSidIcon from "../assets/supply-demand-sid-icon.svg";
import Notification from "./Notification";
import { NOTIFICATION_TAG_TYPES } from "./NotificationTag";
import SidSignalLevelsRow from "./SidSignalLevelsRow";
import withInsiderAnalysisCategories from "../../../../../../hocs/withInsiderAnalysisCategories";
import formatMessageWithShortName from "../../../../../../lib/notifications/formatMessageWithShortName";
import withFullPageLoader from "../../../../../../hocs/withFullPageLoader";
import formatMessageReplaceFirstBracketPair from "../../../../../../lib/notifications/formatMessageReplaceFirstBracketPair";
import {
  getSidSignalLayoutMode,
  SID_SIGNAL_LAYOUT_MODE
} from "../../../../../../lib/notifications/sidSignalDetailTypes";
import { getSupplyDemandNotificationTags } from "../../../../../../lib/notifications/getSupplyDemandNotificationTags";

const NotificationFactory = ({
  notification_type,
  read,
  config,
  message,
  sub_message,
  date,
  translate,
  item,
  locale,
  analysisCategories,
  insiderAnalysisCategories,
  manualStrategies,
  preparedSlugList,
  markAsReadAction,
  onToggle,
  isDesktop,
  startFullPageLoader,
  stopFullPageLoader
}) => {
  const history = useHistory();
  const { action, url, redirectTo } =
    notificationItemFactory({
      item,
      locale,
      analysisCategories,
      insiderAnalysisCategories,
      manualStrategies,
      preparedSlugList
    }) ?? {};

  const common = {
    isDesktop,
    date,
    read,
    subMessage: sub_message,
    onClick: async event => {
      if (url) {
        const currentPathname = window.location.pathname;

        history.push(url);

        if (currentPathname === url) {
          startFullPageLoader();

          setTimeout(() => {
            stopFullPageLoader();
          }, 500);
        }
      }

      if (!read) {
        markAsReadAction({ id: item.id });
      }

      if (!event.ctrlKey && !event.metaKey) {
        onToggle?.();
      }

      if (item.content_type === STOCK_NOTIFICATION_TYPE) {
        return;
      }

      if (redirectTo) {
        window.open(redirectTo, "_blank");
      }

      if (action) {
        await action();
      }
    },
    href: url,
    tags: [
      ...(config?.trend === "up"
        ? [
            {
              text: translate("notification_tag_up"),
              type: NOTIFICATION_TAG_TYPES.BUY
            }
          ]
        : []),
      ...(config?.trend === "down"
        ? [
            {
              text: translate("notification_tag_down"),
              type: NOTIFICATION_TAG_TYPES.SELL
            }
          ]
        : [])
    ]
  };

  switch (notification_type) {
    case NOTIFICATION_TYPES.STOCK_ANALYSIS_CATEGORY_TECHNICAL_ANALYSIS:
      return (
        <Notification
          {...common}
          stock={{
            ticker: config?.ticker,
            market: config?.market,
            name: config?.name
          }}
          title={formatMessageWithShortName({
            message,
            shortName: config?.short_name
          })}
          icon={{
            src: Squaber,
            alt: "Squaber"
          }}
          tags={[
            {
              text: translate("Scenario"),
              type: NOTIFICATION_TAG_TYPES.SCENARIO
            },
            ...common.tags
          ]}
        />
      );
    case NOTIFICATION_TYPES.DEMAND_ZONE:
      return (
        <Notification
          {...common}
          stock={{
            ticker: config?.ticker,
            market: config?.market,
            name: config?.name
          }}
          title={message}
          icon={{
            src: ScaleIcon,
            alt: translate("Scale icon")
          }}
          tags={[
            ...(config?.plot_type
              ? [
                  config?.plot_type === "SUPPLY"
                    ? {
                        text: translate("Supply"),
                        type: NOTIFICATION_TAG_TYPES.SELL
                      }
                    : {
                        text: translate("Demand"),
                        type: NOTIFICATION_TAG_TYPES.BUY
                      }
                ]
              : []),
            ...common.tags
          ]}
        />
      );
    case NOTIFICATION_TYPES.DONCHAIN_H4:
    case NOTIFICATION_TYPES.DONCHAIN_H1:
    case NOTIFICATION_TYPES.ICHIMOKU_KINKO_HYO_H1:
    case NOTIFICATION_TYPES.ICHIMOKU_KINKO_HYO_H4:
      return (
        <Notification
          {...common}
          subtitle={config.title}
          description={message}
          icon={{
            src: TurtleIcon,
            alt: translate("Turtle icon")
          }}
          tags={[
            {
              text: config.market,
              type: NOTIFICATION_TAG_TYPES.ALERT
            },
            ...common.tags
          ]}
        />
      );
    case NOTIFICATION_TYPES.WEBINAR:
      return (
        <Notification
          {...common}
          title={translate("Webinar information")}
          description={message}
          icon={{
            src: WebinarIcon,
            alt: translate("Webinar icon")
          }}
        />
      );
    case NOTIFICATION_TYPES.EDUCATION_EVENT:
      return (
        <Notification
          {...common}
          title={translate("Education events")}
          description={message}
          icon={{
            src: EducationIcon,
            alt: translate("Eduction icon")
          }}
        />
      );
    case NOTIFICATION_TYPES.SPECIAL_OFFER:
      return (
        <Notification
          {...common}
          title={translate("Exclusive deals and special offers")}
          description={message}
          icon={{
            src: OfferIcon,
            alt: translate("Offer icon")
          }}
        />
      );
    case NOTIFICATION_TYPES.THIRD_PARTY_OFFERING:
      return (
        <Notification
          {...common}
          title={translate("Third party offering")}
          description={message}
          icon={{
            src: DealIcon,
            alt: translate("Deal icon")
          }}
        />
      );
    case NOTIFICATION_TYPES.RECOMMENDATIONS:
      return (
        <Notification
          {...common}
          title={translate("Brokerage recommendations")}
          description={message}
          icon={{
            src: RecommendationIcon,
            alt: translate("Recommendation icon")
          }}
        />
      );
    case NOTIFICATION_TYPES.MEDIA_MONITOR:
      return (
        <Notification
          {...common}
          title={message}
          stock={{ ticker: config?.ticker, market: config?.market }}
          tags={[
            ...(config?.source
              ? [
                  {
                    text: config.source,
                    type: NOTIFICATION_TAG_TYPES.ALERT,
                    prefix: `${translate("Source:")} `
                  }
                ]
              : []),
            ...common.tags
          ]}
          icon={{ src: NewspaperIcon, alt: translate("Newspaper icon") }}
        />
      );
    case NOTIFICATION_TYPES.STOCK_ANALYSIS_CATEGORY_VIDEO_ANALYSIS:
      return (
        <Notification
          {...common}
          title={formatMessageReplaceFirstBracketPair(message)}
          description={config?.preview}
          icon={{ src: VideoIcon, alt: translate("Video icon") }}
        />
      );
    case NOTIFICATION_TYPES.STOCK_ANALYSIS_CATEGORY_MORNING_BRIEF:
      return (
        <Notification
          {...common}
          title={formatMessageWithShortName({
            message,
            shortName: config?.short_name
          })}
          description={config?.preview}
          icon={{ src: CoffeeIcon, alt: translate("Coffee icon") }}
        />
      );
    case NOTIFICATION_TYPES.STOCK_ANALYSIS_CATEGORY_FINANCIAL_ANALYSIS:
      return (
        <Notification
          {...common}
          stock={{ ticker: config?.ticker, market: config?.market }}
          title={formatMessageWithShortName({
            message,
            shortName: config?.short_name
          })}
          tags={[
            {
              text: translate("single_analysis"),
              type: NOTIFICATION_TAG_TYPES.ALERT
            },
            ...common.tags
          ]}
          icon={{ src: AnalysisIcon, alt: translate("Analysis icon") }}
        />
      );
    case NOTIFICATION_TYPES.STOCK_ANALYSIS_CATEGORY_SHORT_TECHNICAL_ANALYSIS: {
      return (
        <Notification
          {...common}
          stock={{
            ticker: config?.ticker,
            market: config?.market,
            name: config?.name
          }}
          title={formatMessageWithShortName({
            message,
            shortName: config?.short_name
          })}
          tags={[
            { text: translate("Comment"), type: NOTIFICATION_TAG_TYPES.ALERT },
            ...common.tags
          ]}
          icon={{ src: FeedbackIcon, alt: translate("Feedback icon") }}
        />
      );
    }
    case NOTIFICATION_TYPES.PRICE_ALERTS: {
      return (
        <Notification
          {...common}
          stock={{
            ticker: config?.ticker,
            market: config?.market,
            name: config?.name
          }}
          title={
            <>
              {config?.price ? (
                <>
                  <span className={"font-medium"}>{`${translate(
                    "Price:"
                  )} `}</span>
                  <span className={"font-bold pr-2"}>
                    <FormatCurrency
                      locale={locale}
                      value={config?.price}
                      currency={config?.currency}
                      minimumFractionDigits={2}
                    />
                  </span>
                </>
              ) : null}
              {config?.comment ? <span>{config.comment}</span> : null}
            </>
          }
          icon={{ src: AlertIcon, alt: "Alert icon" }}
        />
      );
    }
    case NOTIFICATION_TYPES.INSIDER_ANALYSIS_CATEGORY_ANALYSIS: {
      return (
        <Notification
          {...common}
          stock={{
            ticker: config?.ticker,
            market: config?.market,
            name: config?.name
          }}
          title={formatMessageWithShortName({
            message,
            shortName: config?.short_name
          })}
          icon={{
            src: AnalysisIcon,
            alt: translate("Analysis icon"),
            tag: {
              type: NOTIFICATION_TAG_TYPES.INSIDER,
              text: "Insider"
            }
          }}
          tags={[
            {
              text: translate("single_analysis"),
              type: NOTIFICATION_TAG_TYPES.ALERT
            },
            ...common.tags
          ]}
        />
      );
    }
    case NOTIFICATION_TYPES.INSIDER_ANALYSIS_CATEGORY_RECOMMENDATION: {
      return (
        <Notification
          {...common}
          stock={{
            ticker: config?.ticker,
            market: config?.market,
            name: config?.name
          }}
          title={formatMessageWithShortName({
            message,
            shortName: config?.short_name
          })}
          icon={{
            src: Squaber,
            alt: "Squaber",
            tag: {
              type: NOTIFICATION_TAG_TYPES.INSIDER,
              text: "Insider"
            }
          }}
          tags={[
            {
              text: translate("Recommendation"),
              type: NOTIFICATION_TAG_TYPES.INSIDER_RECOMMENDATION
            },
            ...common.tags
          ]}
        />
      );
    }
    case NOTIFICATION_TYPES.HIGHS_AND_LOWS_CORRECTION:
    case NOTIFICATION_TYPES.HIGHS_AND_LOWS_SUMMARY: {
      return (
        <Notification
          {...common}
          stock={{
            ticker: config?.ticker,
            market: config?.market,
            name: config?.name
          }}
          title={formatMessageWithShortName({
            message,
            shortName: config?.short_name
          })}
          icon={{
            src: HighsAndLowsIcon,
            alt: translate("Highs and lows icon")
          }}
        />
      );
    }
    case NOTIFICATION_TYPES.TRANSACTION_STRATEGY_HIGHS_AND_LOWS: {
      const layoutMode = getSidSignalLayoutMode(config);
      const beforeDescription = (
        <SidSignalLevelsRow config={config} />
      );

      if (layoutMode === SID_SIGNAL_LAYOUT_MODE.BATCH) {
        return (
          <Notification
            {...common}
            subtitle={translate("notification_sid_strategy_name")}
            beforeDescription={beforeDescription}
            description={message}
            leftAccent
            icon={{
              src: HighsAndLowsSidIcon,
              alt: translate("Highs and lows icon")
            }}
            tags={[
              {
                text: translate("notification_tag_sid_closures"),
                type: NOTIFICATION_TAG_TYPES.ALERT
              },
              ...common.tags
            ]}
          />
        );
      }

      const detailTag =
        layoutMode === SID_SIGNAL_LAYOUT_MODE.ENTRY
          ? {
              text: translate("notification_tag_sid_entry"),
              type: NOTIFICATION_TAG_TYPES.SIGNAL
            }
          : layoutMode === SID_SIGNAL_LAYOUT_MODE.TP1
          ? {
              text: translate("notification_tag_sid_tp1"),
              type: NOTIFICATION_TAG_TYPES.BUY
            }
          : null;

      return (
        <Notification
          {...common}
          stock={{
            ticker: config?.ticker,
            market: config?.market,
            name: config?.name
          }}
          title={formatMessageWithShortName({
            message,
            shortName: config?.short_name
          })}
          beforeDescription={beforeDescription}
          leftAccent
          icon={{
            src: HighsAndLowsSidIcon,
            alt: translate("Highs and lows icon")
          }}
          tags={detailTag ? [detailTag, ...common.tags] : common.tags}
        />
      );
    }
    case NOTIFICATION_TYPES.TRANSACTION_STRATEGY_SUPPLY_DEMAND: {
      const tags = [
        ...getSupplyDemandNotificationTags(config, translate),
        ...common.tags
      ];

      return (
        <Notification
          {...common}
          stock={{
            ticker: config?.ticker,
            market: config?.market,
            name: config?.name
          }}
          title={formatMessageWithShortName({
            message,
            shortName: config?.short_name
          })}
          beforeDescription={<SidSignalLevelsRow config={config} />}
          leftAccent
          icon={{
            src: SupplyDemandSidIcon,
            alt: translate("Scale icon")
          }}
          tags={tags}
        />
      );
    }
    case NOTIFICATION_TYPES.TRANSACTION_STRATEGY_TREND: {
      return (
        <Notification
          {...common}
          subtitle={config?.title || translate("trend_dashboard_heading")}
          description={message}
          leftAccent
          icon={{
            src: TurtleWithSquaberIcon,
            alt: translate("Turtle icon")
          }}
          tags={[
            {
              text: config.market,
              type: NOTIFICATION_TAG_TYPES.ALERT
            },
            ...common.tags
          ]}
        />
      );
    }
    default: {
      return (
        <Notification
          {...common}
          stock={{
            ticker: config?.ticker,
            market: config?.market,
            name: config?.name
          }}
          title={message}
          icon={{ src: Squaber, alt: "Squaber" }}
        />
      );
    }
  }
};
export default compose(
  withPreparedSectionSlugList,
  withTranslations,
  withManualStrategies,
  withAnalysisCategories,
  withInsiderAnalysisCategories,
  withFullPageLoader
)(NotificationFactory);
